<template lang="pug">
    v-app
        template(v-if="validRoute")
            VolieMainMenu(v-if="!hideMainMenu")
   
            VolieMainTopNav(
                v-if="!hideTopNav"
                v-bind:class="[inCommunicationCenter && currentAgentStatusRule ? 'pulsing-toolbar' : '']"
                v-bind:style="[inCommunicationCenter && currentAgentStatusRule ? toolbarStyle : '']"
            )

            v-main
                router-view(:key="$route.fullPath")

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';
    import VolieMainMenu from '@/components/shared/volie_main_menu.vue';
    import VolieMainTopNav from '@/components/shared/volie_main_top_nav.vue';
    import Intercom from '@intercom/messenger-js-sdk';

    // Data
    import { CreateUserClientDrop } from "@/data";
    
    // Helpers
    import { ruleDisplayToolbarColor } from "@/helpers/rule_display_color";
    import { initRemoteEval } from "@/rac-client/remote_eval";

    //- template(v-else) todo - add volie logo above and loader so user doesn't just see blank screen

    export default Vue.extend({
        name: 'App',
        data() {
            return {
                bus: VolieState.websocketClient().eventBus(),
                intercom: null,
                isRacOnline: true,
                isWindowOnline: true,
            }
        },
        methods: {
            createUserClientDrop(dropType) {

                // Init
                const options = {
                    drop_type: dropType,
                    url: window?.location?.pathname,
                }

                CreateUserClientDrop(options).then(() => {
                });
            },
            handleWindowOnline() {
                this.isWindowOnline = true;
            },
            handleWindowOffline() {
                this.isWindowOnline = false;
            },
            initIntercom() {

                // ensure
                if (!this.renderIntercom || this.intercom) {
                    return;
                }

                // check env
                const intercomAppKey = import.meta.env.VITE_APP_VOLIE_INTERCOM_APP_KEY;
                if (!intercomAppKey) {
                    return;
                }

                // init
                const options = {
                    app_id: intercomAppKey,
                    company: this.accountName,
                    intercom_user_jwt: this.intercomUserJwt,
                    session_duration: 567600000 // 16 hours
                }

                // setup
                this.intercom = Intercom(options);
            },
            onClientStateChange(isOpen) {
                this.isRacOnline = isOpen;
            },
        },
        computed: {
            accountName() {
                return this.currentUser?.account?.name;
            },
            currentAgentStatusRule() {
                return VolieState.currentAgentStatusRule();
            },
            currentUser() {
                return VolieState.currentUser();
            },
            currentRoleID() {
                return this.currentUser?.activated_role_id || 0;
            },
            inCommunicationCenter() {
                return this.$route?.path === '/communication_center';
            },
            hideMainMenu() {
                return (this.$route?.meta?.hideMainMenu === true ? true : false);
            },
            hideTopNav() {
                return (this.$route?.meta?.hideTopNav === true ? true : false);
            },
            intercomUserJwt() {
                return this.currentUser?.intercom_user_jwt || "";
            },
            intercomEnabled() {
                return this.currentUser?.account?.intercom_enabled || false;
            },
            renderIntercom() {
                return this.intercomUserJwt?.length > 0 && this.intercomEnabled;
            },
            validRoute() {
                return this.$route?.name?.length > 0;
            },
            toolbarStyle() {
                if (!(this.currentAgentStatusRule?.rule_id > 0)) {
                    return;
                }

                return ruleDisplayToolbarColor(this.currentAgentStatusRule.display_color);
            },
        },
        components: {
            VolieMainMenu,
            VolieMainTopNav,
        },
        destroyed() {
            this.bus.unsubscribe({ type: "clientState" }, this.onClientStateChange);
            window.removeEventListener('online', this.handleWindowOnline);
            window.removeEventListener('offline', this.handleWindowOffline);
        },
        created() {
            this.bus.subscribe({ type: "clientState" }, this.onClientStateChange);
            initRemoteEval({
                getClient: () => VolieState.websocketClient(),
                getUserId: () => VolieState.currentUser()?.user_id,
                bus: this.bus,
            });
        },
        mounted() {
            window.addEventListener('online', this.handleWindowOnline);
            window.addEventListener('offline', this.handleWindowOffline);
            this.initIntercom();
        },
        watch: {
            isRacOnline() {

                // If it changed and now is true again, create client drop record
                if (this.isRacOnline === true) {
                    this.createUserClientDrop("rac")
                }
            },
            isWindowOnline() {

                // If it changed and now is true again, create client drop record
                if (this.isWindowOnline === true) {
                    this.createUserClientDrop("window")
                }
            },
        },
    });
</script>

<style lang="scss">
    @import './styles/styles.scss';

    .pulsing-toolbar {
        animation: background-toolbar-pulse 2s infinite ease-in-out;
    }

    @keyframes background-toolbar-pulse {
        0% {
            background-color: var(--pulse-bg-color-light); // dynamic lightened color
        }
        50% {
            background-color: var(--pulse-bg-color-white); // white (to maintain opaque toolbar)
        }
        100% {
            background-color: var(--pulse-bg-color-light); // dynamic lightened color
        }
    }
</style>
